import { useCallback, useEffect, useState } from "react";
import { DEFAULT_LOCATION } from "../../../lib/drafts";

// useRecorderLocation — geolocation lifecycle for VideoRecorderDialog.
// Extracted from VideoRecorderDialog (Iter-58.6 split) so the dialog
// no longer carries the GPS-fetch + reverse-geocode plumbing inline.
//
// Auto-fetches once when the dialog opens (`open === true` + no location
// yet). Falls back gracefully to DEFAULT_LOCATION on permission denial,
// timeout, or any reverse-geocode failure — the dialog never blocks on
// location, since recording itself never requires it.
//
// Reverse-geocode uses OpenStreetMap's Nominatim (free, no key). It can
// transiently fail; we tolerate that and store lat/lng with placeholder
// city/country so the publish flow can still proceed.
export const useRecorderLocation = ({ open }) => {
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState("");
  const [locationLoading, setLocationLoading] = useState(false);

  const useDefaultLocation = useCallback(() => {
    setLocation(DEFAULT_LOCATION);
    setLocationError("");
    setLocationLoading(false);
  }, []);

  const getLocation = useCallback(() => {
    setLocationLoading(true);
    setLocationError("");
    if (!navigator.geolocation) {
      setLocation(DEFAULT_LOCATION);
      setLocationError("Geolocation not supported — using default location.");
      setLocationLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();
          setLocation({
            lat: latitude,
            lng: longitude,
            city:
              data.address?.city ||
              data.address?.town ||
              data.address?.village ||
              "Unknown",
            country: data.address?.country || "Unknown",
          });
        } catch (_err) {
          // Reverse-geocode failed — keep the coords, mark city/country
          // unknown. The publish flow accepts that shape.
          setLocation({ lat: latitude, lng: longitude, city: "Unknown", country: "Unknown" });
        }
        setLocationLoading(false);
      },
      (error) => {
        let msg = "Couldn't detect location — using default.";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            msg = "Location permission denied — tap the lock icon in your URL bar to allow, then retry.";
            break;
          case error.POSITION_UNAVAILABLE:
            msg = "Location unavailable on this device — using default.";
            break;
          case error.TIMEOUT:
            msg = "Location timed out — tap Retry, or use default.";
            break;
          default:
            break;
        }
        setLocationError(msg);
        setLocation(DEFAULT_LOCATION);
        setLocationLoading(false);
      },
      // Iter-58.11 (2026-06-03): bump to 12s + enableHighAccuracy.
      // Previous 4-second timeout was hitting iOS Safari's cold-start
      // GPS fix (which can take 8-10s on first call after app
      // launch), silently falling back to Demo City. Highaccuracy
      // also nudges iOS toward GPS instead of cell-tower triangulation
      // when the user has actually granted permission.
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  }, []);

  useEffect(() => {
    if (open && !location) getLocation();
  }, [open, location, getLocation]);

  return {
    location,
    setLocation,
    locationError,
    setLocationError,
    locationLoading,
    getLocation,
    useDefaultLocation,
  };
};
